import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '../sections'

/**
 * The DAIMI benchmark, back as a page of its own.
 *
 * Halil, 2026-09-15: *"digitopia.co'da DAIMI Benchmark rapor sayfamızda,
 * Kardelen'in yaptığı bir tool vardı, sektör, ülke seçebiliyorduk. Şimdi düşmüş
 * o, ama çok faydalı idi. Resources altında, direct Benchmark diye dedike bir
 * sayfası olabilir mi onun?"* — and, in the same breath, that DMI and AIMI
 * should be readable beside DAIMI rather than folded into one number.
 *
 * It "fell off" because it was never a URL: a JavaScript widget inside a
 * WordPress page, so the crawl that produced `migration/decisions.json` saw the
 * page and not the tool. There is no row for it to have been dropped from.
 *
 * The widget is `sections/benchmark.tsx`; the data is generated from Pelin's
 * exports by `scripts/generate-benchmark.mjs`, whose header is the place to
 * read about what the data cannot answer yet.
 */
export const route: RouteDef = {
  id: 'benchmark',
  path: 'impact-labs/benchmark',
  priority: 0.8,
}

export const benchmark: WrittenPage = {
  navLabel: 'Benchmark',
  title: 'DAIMI Benchmark',
  description:
    'Digital and AI maturity by sector, country and company size, from Digitopia’s benchmark of 1,575 companies. DAIMI, DMI and AIMI side by side, with the number of companies behind every figure.',
  sections: [
    {
      kind: 'hero',
      // Spelled in capitals rather than left to `text-transform`, for the
      // reason the ARI and ROI pages both record: CSS casing follows the
      // document language and Turkish maps `i` to `İ`.
      eyebrow: 'BENCHMARK',
      headline: 'Where does your sector actually stand?',
      sub: '1,575 companies, 17 sectors, 46 countries. Digital maturity and AI maturity measured separately and then together, so you can see which one your sector is carrying and which one it is not.',
      ctas: [],
    },
    {
      kind: 'benchmark',
      labels: {
        indexHeading: 'The three indices, by sector',
        indexBlurb:
          'DMI measures digital maturity, AIMI measures AI maturity, and DAIMI is the two together — the AI section is one of five in every dimension, so it carries 20% of the combined score. Where the two diverge is usually the more interesting number.',
        indexNote:
          'Averages as published by Digitopia’s benchmark. Companies counted per row; a sector with fewer than five is not scored here.',
        scopeLabel: 'Scope',
        scopes: { global: 'Global', turkiye: 'Türkiye', us: 'United States', APAC: 'APAC', Americas: 'Americas', EMEA: 'EMEA' },
        colSector: 'Sector',
        colCompanies: 'Companies',
        allSectors: 'All sectors',
        tooFew: 'too few companies to publish',
        pickerHeading: 'Your country, your sector, your size',
        pickerBlurb:
          'The six dimensions for a single (country, sector, size) cell. The familiar Digitopia benchmark view, now built on the current data.',
        country: 'Country',
        sector: 'Sector',
        size: 'Company size',
        sizes: { S: 'Small', M: 'Medium', L: 'Large' },
        sourceLine: 'Source study: {source}.',
        noData: 'No companies in the benchmark match that combination yet.',
        loading: 'Loading the benchmark…',
        failed: 'The benchmark data could not be loaded. Reloading the page usually fixes it.',

        vitalityHeading: 'The DAIMI Vitality Chart',
        vitalityBlurb:
          'Every assessed company, plotted on two forces — and DAIMI is the average of the two, so a company’s distance up the diagonal is its score. Velocity is what a company is doing — how it serves customers, how it runs, how it innovates. Endurance is what lets it keep doing it — its people, its governance, its technology. Speed without endurance stalls; endurance without speed never starts.',
        vitalityNote:
          'One dot per assessed company, 1,575 in total, anonymised to a company ID. DMI measures digital maturity, AIMI measures AI maturity, and DAIMI is the two together — the AI section is one of five in every dimension, so it carries 20% of the combined score. The dashed lines are the global averages, 2.92 Velocity and 2.93 Endurance, and mark the four profiles; they were not on the original chart.',
        allOption: 'All',
        industry: 'Main industry',
        axisVelocity: 'Velocity',
        axisEndurance: 'Endurance',
        figureTitle: 'Figure: DAIMI Vitality Chart',
        companyCount: 'Company count:',
        resetFilter: 'Reset filter',
        companyId: 'Company ID',
        derivedNote: 'Derived from the six dimensions above.',
        reportEyebrow: 'Industry Focus Report',
        reportTitle: 'Dive deeper into the {industry} DAIMI Report',
        reportButton: 'Read the report',
        indexLabel: 'Index',
        indexNames: { daimi: 'DAIMI', dmi: 'DMI', aimi: 'AIMI' },
        indexAverage: 'Average {index} of the {n} companies shown.',
        aboveAverage: '{n} at or above the benchmark {index} of {avg}',
        belowAverage: '{n} below it',
        estimatedNote:
          '* The AI and digital split is measured for each country / sector / size group and applied to a company’s own DAIMI — a measurement per group, an estimate per company.',
        noSplit: '{n} companies are not shown: their group has no split in the source.',
        profiles: {
          ironMan: {
            name: 'Iron Man',
            blurb:
              'Moving fast on foundations that hold. Both scores above the global average — the combination that turns a transformation programme into a way of operating.',
          },
          marathon: {
            name: 'Marathon Runner',
            blurb:
              'The foundations are there — people, governance, technology — but little is being run on them yet. The capacity to change exists ahead of the appetite.',
          },
          sprinter: {
            name: 'Sprinter',
            blurb:
              'Visible momentum on customers, operations and innovation, built on foundations that have not kept up. The pace is real; it is borrowed.',
          },
          walker: {
            name: 'Walker',
            blurb:
              'Below the global average on both. Neither the moves nor the capacity to sustain them — where most of the benchmark still sits.',
          },
        },
      },
    },
    {
      kind: 'prose',
      check:
        'THE INDICES ARE SECTOR-LEVEL ONLY, AND THAT IS A DATA LIMIT RATHER '
        + 'THAN A DESIGN CHOICE. DMI/AIMI/DAIMI can be computed per country from '
        + 'the topic scores and the AI section map — globally that reproduces the '
        + 'official AIMI to 0.01, but for the US it lands 0.13 low, because the '
        + 'official figure weights by company and no export carries a company '
        + 'count per (country, sector, size) cell. Asked of Pelin 2026-09-16. '
        + 'When `Company_Count` arrives at that grain the picker can show all '
        + 'three indices too, and this note goes.\n\n'
        + 'TURKISH IS WRITTEN for the page and the widget labels. The sector and '
        + 'country names come from the source data and are English in both '
        + 'locales, like the tools’ industry lists.\n\n'
        + 'THE VITALITY CHART’S FOUR PROFILES ARE OURS, NOT KARDELEN’S. Fatih '
        + 'sent the original chart block on 2026-09-16 and it has no quadrants '
        + 'at all — plain 1–5 axes, one colour, and the Iron Man / Marathon '
        + 'Runner / Sprinter / Walker boxes sat elsewhere on the page with '
        + 'nothing tying them to the dots. So the dashed lines here are the '
        + 'published global averages (2.92 / 2.93) and the names are mapped to '
        + 'the corners by the metaphor. Halil or Mert to confirm both, or say '
        + 'where the report actually cuts them; each is one line to change. '
        + 'Asked 2026-09-16.\n\n'
        + 'THE OLD DATA FILE IS GONE FROM THE LIVE WEB. The chart on '
        + 'digitopia.co fetched its 1,575 rows from a wp-content URL; that host '
        + 'now 301s to www.digitopia.ai, which 404s the path, so the original '
        + 'chart had already stopped working before this was built. '
        + '`migration/benchmark/daimi-companies.json` is the surviving copy.',
      paragraphs: [],
    },
    {
      kind: 'prose',
      heading: 'How to read these numbers',
      paragraphs: [
        'Every score is on a 1–5 scale, and the distance that matters is small: the gap between the strongest sector and the weakest here is about three quarters of a point. A sector at 3.35 and one at 2.60 are not two versions of the same company with different budgets — they are organisations that decided different things about data, about who owns a customer problem, and about whether a pilot is allowed to end.',
        'Read DMI and AIMI apart before you read DAIMI. A sector can hold a respectable digital score while its AI score sits a full point below it, and that gap is the honest picture of where most companies are: the foundations largely built, the capability on top of them barely started. Banking is the one place where the two are close. Tourism is the clearest case of the opposite.',
      ],
    },
  ],
}
